import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Base64 } from 'js-base64';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { APP_ROOT_DOM_NODE_ID, IS_DEV_ENV, IS_NODE_SERVER, SENTRY_DSN } from './constants/config';

import { createStore, applyMiddleware, Middleware, Store, Action } from 'redux';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import thunkMiddleware from 'redux-thunk';
import { createLogger } from 'redux-logger';

import rootReducer from './reducers';
import { StoreState } from './types';
import { attributionMiddleware } from './redux-middleware/attribution';

import App, { AppHelmet } from './App';
import AppLogger from './utils/logging';
const logger = AppLogger.createLogger();

const history = createBrowserHistory();
const loggerMiddleware = createLogger();

const middleware: Array<Middleware> = [
    thunkMiddleware, // lets us dispatch() functions
    // routerMiddleware(history), // React router middelware
];

Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
});

if (process.env.NODE_ENV === 'development' && false) {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    whyDidYouRender(React, {
        trackAllPureComponents: true,
    });
}

if (IS_DEV_ENV) {
    // add state logging to console during development
    middleware.push(loggerMiddleware);

    // this flag determines if public/index.html is
    // used to serve js
    // include css files from old codebase

    // ==================================> !!ATTENTION!!
    // you should create symlinks:
    // twsapps/static/scss -> react-app/src/scss
    // twsapps/static/css -> react-app/src/css
    // require('./css/bootstrap3/bootstrap.css');
    // require('./scss/weddingspot.scss');
    // require('./scss/photo-management.scss');
}

if (!IS_NODE_SERVER) {
    middleware.push(
        attributionMiddleware // tracks UTM/HTTP Referer attribution via product API
    );
}

declare let window: {
    __PRELOADED_STATE__?: string;
    __IS_BOT__?: boolean;
    __PROFILE_VERSION__?: string;
    __META_ROBOTS__?: string;
    __DD_SERVICE__: string;
    __DD_ENV__: string;
    __DD_VERSION__: string;
};

const getStore = function (): Store<StoreState> {
    const encodedState = window.__PRELOADED_STATE__;
    let windowState;
    if (encodedState) {
        windowState = JSON.parse(Base64.decode(encodedState));
    }
    if (!!windowState && Object.keys(windowState).length > 0) {
        logger.info('Found pre-loaded state on window');
        delete window.__PRELOADED_STATE__;
        return createStore<StoreState, Action<any>, {}, {}>(rootReducer, windowState, applyMiddleware(...middleware));
    }
    logger.info('No pre-loaded state, generating new state');
    return createStore<StoreState, Action<any>, {}, {}>(rootReducer, applyMiddleware(...middleware));
};

const store = getStore();
const rootNode = document.getElementById(APP_ROOT_DOM_NODE_ID) as HTMLElement;
const renderOrHydrate = rootNode.innerHTML.trim().length ? 'hydrate' : 'render';
ReactDOM[renderOrHydrate](
    <Provider store={store}>
        <BrowserRouter>
            <App 
                history={history} 
                isBot={window.__IS_BOT__} 
                profileVersion={window.__PROFILE_VERSION__} 
                metaRobots={window.__META_ROBOTS__} 
                ddService={window.__DD_SERVICE__}
                ddEnv={window.__DD_ENV__}
                ddVersion={window.__DD_VERSION__}
            />
        </BrowserRouter>
    </Provider>,
    rootNode
);

logger.info('HELMET CLIENT: ' + JSON.stringify(AppHelmet.peek()));
// TODO: This won't work in prod because we serve our static assets
// from our cdn.wedding-spot.com domain which the service worker doesn't like
// registerServiceWorker();
